import {
  cloneElement,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from "react";
import type {
  EditorialEditorModel,
  FinanceEditorModel,
  ServiceEditorModel,
} from "@/lib/domain/editor";
import { RelationPicker } from "../relation-picker";

type EditorModel =
  | ServiceEditorModel
  | FinanceEditorModel
  | EditorialEditorModel;

type ChangeEditor = (
  updater: (current: EditorModel) => EditorModel,
) => void;

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const generatedId = useId();
  const control = isValidElement(children)
    ? (children as ReactElement<{ id?: string }>)
    : null;
  const controlId = control?.props.id ?? generatedId;

  return (
    <div className={`ui-field${className ? ` ${className}` : ""}`}>
      <label htmlFor={controlId}>{label}</label>
      {control ? cloneElement(control, { id: controlId }) : children}
    </div>
  );
}

export function DomainRelations({
  model,
  change,
}: {
  model: EditorModel;
  change: ChangeEditor;
}) {
  if (model.kind === "service") return null;

  if (model.kind === "finance") {
    return null;
  }

  return (
    <div className="relations-grid">
      <RelationPicker
        label="Koleksiyonlar"
        options={model.options.editorialCollections}
        values={model.record.collectionIds}
        onChange={(values) =>
          change((current) =>
            current.kind === "editorial"
              ? {
                  ...current,
                  record: { ...current.record, collectionIds: values },
                }
              : current,
          )
        }
      />
      <RelationPicker
        label="Etiketler"
        options={model.options.editorialTags}
        values={model.record.tagIds}
        onChange={(values) =>
          change((current) =>
            current.kind === "editorial"
              ? { ...current, record: { ...current.record, tagIds: values } }
              : current,
          )
        }
      />
      <RelationPicker
        label="Sektörler"
        options={model.options.sectors}
        values={model.record.sectorIds}
        onChange={(values) =>
          change((current) =>
            current.kind === "editorial"
              ? { ...current, record: { ...current.record, sectorIds: values } }
              : current,
          )
        }
      />
      <div className="form-grid">
        <Field label="Kategori">
          <select
            className="ui-select"
            value={model.record.categoryId || ""}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      record: {
                        ...current.record,
                        categoryId: event.target.value || null,
                      },
                    }
                  : current,
              )
            }
          >
            <option value="">Kategori yok</option>
            {model.options.editorialCategories.map((option) => (
              <option value={option.id} key={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Yazar">
          <select
            className="ui-select"
            value={model.record.authorId || ""}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      record: {
                        ...current.record,
                        authorId: event.target.value || null,
                      },
                    }
                  : current,
              )
            }
          >
            <option value="">Yazar yok</option>
            {model.options.authors.map((option) => (
              <option value={option.id} key={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}

export function EditorialFormatFields({
  model,
  change,
}: {
  model: EditorialEditorModel;
  change: ChangeEditor;
}) {
  if (model.record.format === "video") {
    return (
      <>
        <Field label="Video URL">
          <input
            className="ui-input"
            value={model.content.videoUrl}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        videoUrl: event.target.value,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        <Field label="Etkinlik tarihi">
          <input
            className="ui-input"
            type="datetime-local"
            value={model.content.eventDate?.slice(0, 16) || ""}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        eventDate: event.target.value || null,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        <Field label="Süre (saniye)">
          <input
            className="ui-input"
            type="number"
            value={model.content.durationSeconds ?? ""}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        durationSeconds: event.target.value
                          ? Number(event.target.value)
                          : null,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        <Field label="Transkript" className="full">
          <textarea
            className="ui-textarea"
            value={model.content.transcript}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        transcript: event.target.value,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
      </>
    );
  }

  if (model.record.format === "resource") {
    return (
      <>
        <Field label="Kaynak sürümü">
          <input
            className="ui-input"
            value={model.content.resourceVersion}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        resourceVersion: event.target.value,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        {model.content.resourceUrl ? (
          <p className="ui-field full">
            Mevcut dosya: <a href={model.content.resourceUrl}>Dosyayı aç</a>
          </p>
        ) : null}
      </>
    );
  }

  if (model.record.format === "training") {
    return (
      <>
        <Field label="Etkinlik tarihi">
          <input
            className="ui-input"
            type="datetime-local"
            value={model.content.eventDate?.slice(0, 16) || ""}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        eventDate: event.target.value || null,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        <Field label="Süre (dakika)">
          <input
            className="ui-input"
            type="number"
            value={model.content.durationMinutes ?? ""}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        durationMinutes: event.target.value
                          ? Number(event.target.value)
                          : null,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        <Field label="Kazanımlar" className="full">
          <textarea
            className="ui-textarea"
            value={model.content.outcomes}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        outcomes: event.target.value,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
      </>
    );
  }

  if (model.record.format === "external_news") {
    return (
      <>
        <Field label="Kaynak URL">
          <input
            className="ui-input"
            type="url"
            value={model.content.sourceUrl}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        sourceUrl: event.target.value,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
        <Field label="Yayın adı">
          <input
            className="ui-input"
            value={model.content.publicationName}
            onChange={(event) =>
              change((current) =>
                current.kind === "editorial"
                  ? {
                      ...current,
                      content: {
                        ...current.content,
                        publicationName: event.target.value,
                      },
                    }
                  : current,
              )
            }
          />
        </Field>
      </>
    );
  }

  return null;
}
